import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { FilePdfOutlined } from "@ant-design/icons";

import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import DataTable from "../components/DataTable";
import RegisterManual from "../components/RegisterManual";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Utils from "../utils/utils";
import * as Common from "../commons/common";

type DataIndex = keyof Types.ManualDto;

function ManualList() {
    const alert = useAlert();

    const [manuals, setManuals] = useState<Array<Types.ManualDto>>();
    const [currentManual, setcurrentManual] = useState<Types.ManualDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteManual, setOpenDeleteManual] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.ManualDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.ManualDto> = [
        {
            align: "center",
            title: String.manualId,
            dataIndex: "manualId",
            key: "manualId",
            ...getColumnSearchProps("manualId"),
        },
        {
            align: "center",
            title: String.manualVersion,
            dataIndex: "manualVersion",
            key: "manualVersion",
        },
        {
            align: "center",
            title: String.manualName,
            dataIndex: "manualName",
            key: "manualName",
            ...getColumnSearchProps("manualName"),
        },
        {
            align: "center",
            title: String.manualFileName,
            dataIndex: "manualFileName",
            key: "manualFileName",
            render: (_, { manualId, manualFileName }) => {
                return (
                    <a
                        onClick={() => {
                            handleDownloadManualOk(manualId, manualFileName);
                        }}
                    >
                        {" "}
                        <FilePdfOutlined /> {manualFileName}
                    </a>
                );
            },
        },
    ];

    const requestAxiosGetManuals = async () => {
        const response = await utilAxiosWithAuth().get(Request.MANUAL_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetManuals,
        error: errorGetManuals,
        data: resultGetManuals,
        execute: requestGetManuals,
    } = useAsyncAxios(requestAxiosGetManuals);

    useEffect(() => {
        if (resultGetManuals === null) return;

        setManuals(resultGetManuals.manuals);
    }, [resultGetManuals]);

    useEffect(() => {
        if (errorGetManuals === null) return;

        alert.setAlert(errorGetManuals.response.data.code, "Error", errorGetManuals.response.data.message);
    }, [errorGetManuals]);

    const requestAxiosRemoveManual = async (data: Types.ManualDto) => {
        const response = await utilAxiosWithAuth().delete(Request.MANUAL_URL + "/" + data.manualId);
        return response.data;
    };

    const {
        loading: loadingRemoveManual,
        error: errorRemoveManual,
        data: resultRemoveManual,
        execute: requestRemoveManuals,
    } = useAsyncAxios(requestAxiosRemoveManual);

    useEffect(() => {
        if (resultRemoveManual === null) return;

        alert.setAlert(AlertType.SUCCESS, "매뉴얼 삭제 성공", "매뉴얼 삭제를 완료하였습니다.");

        requestGetManuals();
    }, [resultRemoveManual]);

    useEffect(() => {
        if (errorRemoveManual === null) return;

        alert.setAlert(errorRemoveManual.response.data.code, "Error", errorRemoveManual.response.data.message);
    }, [errorGetManuals]);

    useEffect(() => {
        requestGetManuals();
    }, []);

    const onRow = (value: Types.ManualDto) => {
        setEditMode("none");
        setcurrentManual(value);
    };

    const handleDeleteManual = () => {
        if (currentManual !== undefined) setOpenDeleteManual(true);
    };

    const handleDeleteManualOk = () => {
        setOpenDeleteManual(false);
        requestRemoveManuals(currentManual);
    };

    const handleDeleteManualCancel = () => {
        setOpenDeleteManual(false);
    };

    const handleDownloadManualOk = (manualId: number, manualFileName: string) => {
        console.log("id", manualId);

        const token = JSON.parse(Utils.getLocalStorage(Common.CONTEXT_AUTH) || "{}").token;

        fetch(Request.MANUAL_DOWNLOAD_URL + "/" + manualId, {
            headers: {
                "Content-Type": "application/octet-stream",
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            response.arrayBuffer().then((arrayBuffer) => {
                saveByteArray(manualFileName, new Uint8Array(arrayBuffer));
            });
        });
    };

    function saveByteArray(fileName: any, byte: any) {
        const blob = new Blob([byte], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    }

    const onRegisterManual = () => {
        requestGetManuals();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>매뉴얼</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteManual()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="manualId" dataSource={manuals} onRow={onRow} />
            {editMode === "add" && <RegisterManual editMode={editMode} onRegisterManual={onRegisterManual} />}
            {editMode !== "add" && currentManual !== undefined && (
                <RegisterManual editMode={editMode} manual={currentManual} onRegisterManual={onRegisterManual} />
            )}

            {openDeleteManual && (
                <Modal open={openDeleteManual} onOk={handleDeleteManualOk} onCancel={handleDeleteManualCancel}>
                    {currentManual?.manualName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default ManualList;
